import React from 'react';
import { ChevronRight } from 'lucide-react';
import { useLanguage, useTranslate } from '../lib/i18n';
import { useInfoCardAudioAvailability } from '../hooks/queries/useInfoCards';

interface Props {
  onTopicSelect: (topic: string) => void;
  highlightedTopic: string | null | undefined;
  stackIds: string[];
  topics: string[];
}

const TopicSelector = ({ onTopicSelect, highlightedTopic, stackIds, topics }: Props) => {
  const t = useTranslate();
  const { language } = useLanguage();
  const { data: hasAudioAvailable } = useInfoCardAudioAvailability(language, stackIds);

  const subtitleKey =
    hasAudioAvailable === false ? 'topicSelector.subtitleNoAudio' : 'topicSelector.subtitle';

  return (
    <div className="relative overflow-hidden">
      <div>
        <h3 className="text-center text-2xl font-bold text-card-foreground">
          {t('topicSelector.title')}
        </h3>
        <p className="mb-4 text-center text-base text-muted-foreground">{t(subtitleKey)}</p>
        <p className="mb-6 text-center text-sm text-muted-foreground">
          {t('topicSelector.description')}
        </p>
        <div className="flex flex-col items-center gap-4">
          <div className="w-full max-w-[500px] px-6">
            <div className="space-y-4 pb-3 text-start">
              {(topics ?? []).map((topic) => (
                <div
                  key={topic}
                  onClick={() => onTopicSelect(topic)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onTopicSelect(topic);
                    }
                  }}
                  className={`flex cursor-pointer items-center justify-between rounded border border-secondary bg-secondary px-4 py-3 text-secondary-foreground transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset ${
                    highlightedTopic === topic
                      ? 'scale-[1.02] border-2 bg-secondary/90 shadow-lg'
                      : 'hover:scale-[1.01] hover:bg-secondary/90'
                  }`}
                >
                  <span className="grow text-center">{t('topics.' + topic)}</span>
                  <ChevronRight
                    className={`h-4 w-4 transition-transform duration-200 ${
                      highlightedTopic === topic ? 'translate-x-1' : ''
                    }`}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopicSelector;
